import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import FormComponent from 'components/UI/Input/FormComponent';
import Container from 'components/UI/Layout/Container';
import Row from 'components/UI/Layout/Row';
import Col from 'components/UI/Layout/Col';
import EditableTextInput from 'components/UI/Input/EditableTextInput/index';
import produce from 'immer';
import { common as resetEmailRules } from 'services/validation/rules/resetEmail.rules';
import Form from 'services/validation/Form';
import { faChevronRight, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MembersRepository from 'services/repositories/MembersRepository';
import _ from 'lodash';
import ErrorMessage from 'components/UI/ErrorMessage';
import LoadingSpinner from 'components/UI/LoadingIndicators/LoadingSpinner';

function ForgotPasswordForm({ onSuccess })
{
    const [form, setForm] = useState(new Form({
        email: '',
    }, resetEmailRules));
    const [fetchedResetEmail, setFetchedResetEmail] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleFormChange = (_form, path) =>
    {
        const updatedForm = produce(_form, (draft) =>
        {
            draft.data[path] = _form.data[path];
        });

        setForm(updatedForm);
    };

    const fetchResetEmail = async (_form) =>
    {
        const data = {
            email: _form.data.email,
        };

        setIsLoading(true);
        (new MembersRepository()).resetPassword(data)
            .then((response) =>
            {
                if (!response.isSuccess())
                {
                    setErrorMessage(_.get(response, 'messages.0', null) ?? 'Whoops something went wrong. If the error continues, contact technical support.');
                    return;
                }

                if (onSuccess)
                {
                    onSuccess();
                }

                setFetchedResetEmail(true);
            }).catch((e) =>
            {
                setErrorMessage(e.message ?? 'Whoops something went wrong. If the error continues, contact technical support.');
            })
            .finally(() =>
            {
                setIsLoading(false);
            });
    };

    const handleResetPassword = () =>
    {
        const _form = produce(form, (draft) =>
        {
            draft.validate();
        });

        setForm({ ..._form });
        if (!_form.isValid())
        {
            return;
        }

        fetchResetEmail(_form);
    };

    return (
        <FormComponent source={form} onFormChange={handleFormChange}>
            <Container className="tw-px-4 tw-mb-4 tw-rounded-md">
                { fetchedResetEmail
                    && (
                        <Row className="tw-mb-3 tw-mt-3 tw-justify-center">
                            <p>
                                We have sent instructions on how to reset your password to your email address.
                            </p>
                        </Row>
                    )}

                { !fetchedResetEmail && (
                    <>
                        <ErrorMessage errorMessage={errorMessage} />

                        <Row className="tw-mb-3 tw-justify-center">
                            <span className="tw-font-regular">Please enter your email.</span>
                        </Row>

                        <Row className="tw-mb-3 tw-w-full">
                            <Col className="tw-w-full">
                                <EditableTextInput
                                    editing={!isLoading}
                                    labelText="Email: "
                                    text={form.data.email}
                                    inputProps={{
                                        type: 'email',
                                        path: 'email',
                                        error: form.errorsBag.errors.email,
                                        icon: faEnvelope,
                                    }}
                                />
                            </Col>
                        </Row>

                        <Row className="tw-text-right tw-mt-5" reversed>
                            <Button
                                className="tw-py-2"
                                onClick={() => handleResetPassword()}
                                disabled={isLoading}
                            >
                                Next {!isLoading && <FontAwesomeIcon className="tw-ml-4" icon={faChevronRight} />} <LoadingSpinner className="tw-ml-4" loading={isLoading} />
                            </Button>
                        </Row>
                    </>
                )}
            </Container>
        </FormComponent>
    );
}

ForgotPasswordForm.propTypes = {
    onSuccess: PropTypes.func,
};

export default ForgotPasswordForm;
